import { useState, useRef, useEffect, type ReactNode } from 'react';
import { Pencil, X, Check, ChevronDown, ChevronRight, Circle, ArrowUp } from 'lucide-react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { z } from 'zod';
import { Button } from '@reflex/ui/components/ui/button';
import { Textarea } from '@reflex/ui/components/ui/textarea';
import { AttachmentChip } from '@reflex/ui/components/shared/AttachmentChip';
import { ConfirmDialog } from '@reflex/ui/components/shared/ConfirmDialog';
import { ImageLightbox } from '@reflex/ui/components/content/ImagePreview';
import { cn } from '@reflex/ui/lib/utils';
import { AttachmentSchema, type Attachment } from '@runloop/reflex-contract';
import type { QueuedMessage } from '@reflex/ui/client/hooks';
import { useAttachmentUrls } from '../useAttachmentUrl';

const AttachmentListSchema = z.array(AttachmentSchema);

// The plugin-facing QueuedMessage type doesn't declare `attachments` (it's an
// index-signature field), so parse it before rendering instead of casting.
function queuedAttachments(message: QueuedMessage): Attachment[] {
  const parsed = AttachmentListSchema.safeParse(message.attachments);
  return parsed.success ? parsed.data : [];
}

/**
 * Renders a queued message's attachments: images become clickable chips that
 * open the full-size {@link ImageLightbox} — the same viewer the chat stream
 * uses — so a queued image can be inspected before it's sent. The message's
 * images share one viewer, opened on the chip that was clicked, so a message
 * carrying three screenshots pages through them instead of forcing three
 * separate round-trips through the chip list.
 *
 * Presigned URLs resolve lazily via {@link useAttachmentUrls}; a chip whose
 * URL hasn't resolved (or has expired) stays inert rather than opening an
 * empty dialog. When `onRemove` is wired each chip also carries a remove
 * control, so a file attached to the wrong message can be dropped without
 * deleting the message and retyping it.
 */
function QueuedAttachments({
  attachments,
  className,
  onRemove,
}: {
  attachments: Attachment[];
  className?: string;
  onRemove?: (attachmentId: string) => void;
}) {
  const images = attachments.filter((att) => att.type === 'image');
  const resolved = useAttachmentUrls(images);
  // Which image the viewer is open on, held by attachment id rather than
  // position: URLs resolve one by one, so an index would point at a different
  // image as the gallery fills in, and at nothing once a chip is removed.
  const [openId, setOpenId] = useState<string | null>(null);
  const retriedIds = useRef(new Set<string>());

  // Only images that actually resolved can be shown, so the gallery is built
  // from those alone and a chip whose URL is missing has no click target.
  const viewable = images.flatMap((att, i) => {
    const { url, isExpired, refresh } = resolved[i]!;
    return url && !isExpired ? [{ id: att.id, src: url, alt: att.name, refresh }] : [];
  });
  const viewableIds = new Set(viewable.map((image) => image.id));
  const openIndex = viewable.findIndex((image) => image.id === openId);

  // Refresh a presigned URL at most once per attachment on a load failure.
  // Without the guard a persistently-failing image would loop
  // error → refresh → refetch → error.
  const handleLoadError = (index: number) => {
    const image = viewable[index];
    if (!image || retriedIds.current.has(image.id)) return;
    retriedIds.current.add(image.id);
    image.refresh();
  };

  if (attachments.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {attachments.map((att) => (
        <AttachmentChip
          key={att.id}
          attachment={att}
          onClick={viewableIds.has(att.id) ? () => setOpenId(att.id) : undefined}
          onRemove={onRemove && (() => onRemove(att.id))}
        />
      ))}
      {openIndex !== -1 && (
        <ImageLightbox
          images={viewable}
          initialIndex={openIndex}
          open
          onOpenChange={(open) => {
            if (!open) setOpenId(null);
          }}
          onLoadError={handleLoadError}
        />
      )}
    </div>
  );
}

interface QueuedMessageBlockProps {
  messages: QueuedMessage[];
  onEdit: (id: string, text: string, attachments?: Attachment[]) => void;
  onDelete: (id: string) => void;
  onReorder?: (orderedIds: string[]) => void;
  /**
   * Send a queued message ahead of the queue by redirecting the running agent.
   * Optional: when omitted (e.g. replay), rows only show edit + remove.
   */
  onSendNow?: (id: string) => void;
}

export function QueuedMessageBlock({
  messages,
  onEdit,
  onDelete,
  onReorder,
  onSendNow,
}: QueuedMessageBlockProps) {
  const [expanded, setExpanded] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (messages.length === 0) return null;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorder) return;
    const ids = messages.map((m) => m.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onReorder(arrayMove(ids, from, to));
  };

  const rows = messages.map((msg) => (
    <SortableQueuedMessageRow
      key={msg.id}
      message={msg}
      onEdit={onEdit}
      onDelete={onDelete}
      onSendNow={onSendNow}
      sortable={Boolean(onReorder)}
    />
  ));

  return (
    <div className="py-1.5">
      <div className="rounded-lg border border-border/60 bg-muted/20">
        <Button
          variant="ghost"
          onClick={() => setExpanded((v) => !v)}
          aria-label="Toggle queued messages"
          aria-expanded={expanded}
          className="h-auto w-full justify-start gap-1.5 rounded-b-none rounded-t-lg px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-muted/40 hover:text-muted-foreground dark:hover:bg-muted/40"
        >
          {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          <span>{messages.length} Queued</span>
        </Button>
        {/* DndContext wraps the <ul> rather than living inside it: dnd-kit
            injects screen-reader live-region nodes beside its children, and
            a <ul> may only contain <li> (axe `list`). */}
        {expanded &&
          (onReorder ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={messages.map((m) => m.id)}
                strategy={verticalListSortingStrategy}
              >
                <ul className="flex flex-col">{rows}</ul>
              </SortableContext>
            </DndContext>
          ) : (
            <ul className="flex flex-col">{rows}</ul>
          ))}
      </div>
    </div>
  );
}

interface SortableQueuedMessageRowProps {
  message: QueuedMessage;
  onEdit: (id: string, text: string, attachments?: Attachment[]) => void;
  onDelete: (id: string) => void;
  onSendNow?: (id: string) => void;
  sortable: boolean;
}

function SortableQueuedMessageRow({
  message,
  onEdit,
  onDelete,
  onSendNow,
  sortable,
}: SortableQueuedMessageRowProps) {
  const [editing, setEditing] = useState(false);

  const sortableState = useSortable({ id: message.id, disabled: !sortable || editing });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortableState;

  const style = sortable
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
      }
    : undefined;

  const handleStartEdit = () => setEditing(true);
  const handleStopEdit = () => setEditing(false);

  /**
   * Drop a single attachment from the pending message. The edit carries the
   * attachments that survive, so the server replaces the stored list. A
   * message with no text was only ever its attachments: removing the last one
   * would leave an empty row nobody can send, so delete it instead.
   */
  const handleRemoveAttachment = (attachmentId: string) => {
    const remaining = queuedAttachments(message).filter((att) => att.id !== attachmentId);
    if (remaining.length === 0 && message.text.trim().length === 0) {
      onDelete(message.id);
      return;
    }
    onEdit(message.id, message.text, remaining);
  };

  return (
    <li
      ref={sortable ? setNodeRef : undefined}
      style={style}
      className={cn(
        'border-t border-border/40 first:border-t-0',
        isDragging && 'relative z-10 bg-background shadow-md',
      )}
    >
      {editing ? (
        <QueuedMessageEditor message={message} onEdit={onEdit} onClose={handleStopEdit} />
      ) : (
        <QueuedMessageRowContent
          message={message}
          onDelete={onDelete}
          onSendNow={onSendNow}
          onStartEdit={handleStartEdit}
          onRemoveAttachment={handleRemoveAttachment}
          dragHandle={
            sortable
              ? {
                  attributes,
                  listeners,
                }
              : undefined
          }
        />
      )}
    </li>
  );
}

interface QueuedMessageEditorProps {
  message: QueuedMessage;
  onEdit: (id: string, text: string, attachments?: Attachment[]) => void;
  onClose: () => void;
}

function QueuedMessageEditor({ message, onEdit, onClose }: QueuedMessageEditorProps) {
  const [editText, setEditText] = useState(message.text);
  const attachments = queuedAttachments(message);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.selectionStart = textareaRef.current.value.length;
    }
  }, []);

  const handleSave = () => {
    const trimmed = editText.trim();
    if (trimmed) {
      onEdit(message.id, trimmed);
    }
    onClose();
  };

  const handleCancel = () => {
    setEditText(message.text);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className="flex flex-col gap-1 px-3 py-2">
      <QueuedAttachments attachments={attachments} />
      <Textarea
        ref={textareaRef}
        value={editText}
        onChange={(e) => setEditText(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={2}
        className="max-h-80 resize-y rounded border-border bg-background px-2 py-1 text-sm md:text-sm dark:bg-background"
      />
      <div className="flex justify-end gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={handleCancel}
          aria-label="Cancel edit"
        >
          <X className="icon-xs" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={handleSave}
          aria-label="Save"
        >
          <Check className="icon-xs" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Shows what is about to be discarded inside the confirm dialog: the message
 * text, clamped so a long message can't push the dialog buttons off-screen,
 * plus a count of the attachments that go with it. An attachment-only message
 * has no text to show, so the count carries the whole preview.
 */
function QueuedMessagePreview({
  message,
  attachments,
}: {
  message: QueuedMessage;
  attachments: Attachment[];
}) {
  const text = message.text.trim();
  if (!text && attachments.length === 0) return null;
  return (
    <div className="rounded border border-border/60 bg-muted/30 px-2.5 py-2">
      {text && (
        <p className="line-clamp-4 whitespace-pre-wrap wrap-break-word text-muted-foreground">
          {text}
        </p>
      )}
      {attachments.length > 0 && (
        <p className={cn('text-xs text-muted-foreground', text && 'mt-1.5')}>
          {attachments.length} attachment{attachments.length === 1 ? '' : 's'}
        </p>
      )}
    </div>
  );
}

interface QueuedMessageRowContentProps {
  message: QueuedMessage;
  onDelete: (id: string) => void;
  onSendNow?: (id: string) => void;
  onStartEdit: () => void;
  onRemoveAttachment: (attachmentId: string) => void;
  dragHandle?: {
    attributes: ReturnType<typeof useSortable>['attributes'];
    listeners: ReturnType<typeof useSortable>['listeners'];
  };
}

function QueuedMessageRowContent({
  message,
  onDelete,
  onSendNow,
  onStartEdit,
  onRemoveAttachment,
  dragHandle,
}: QueuedMessageRowContentProps): ReactNode {
  const attachments = queuedAttachments(message);
  return (
    <div className="group flex items-start gap-2 px-3 py-2">
      <div
        {...(dragHandle?.attributes ?? {})}
        {...(dragHandle?.listeners ?? {})}
        className={cn(
          'flex flex-1 items-start gap-2',
          dragHandle && 'cursor-grab select-none active:cursor-grabbing',
        )}
      >
        <Circle className="icon-sm mt-0.5 shrink-0 text-muted-foreground/70" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          {message.text && (
            <p className="line-clamp-2 whitespace-pre-wrap wrap-break-word text-sm">
              {message.text}
            </p>
          )}
          <QueuedAttachments
            attachments={attachments}
            className={cn(message.text && 'mt-1.5')}
            onRemove={onRemoveAttachment}
          />
        </div>
      </div>
      <div className="flex shrink-0 gap-0.5 opacity-100 transition-opacity md:opacity-60 md:group-hover:opacity-100 md:group-focus-within:opacity-100">
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-foreground"
          onClick={onStartEdit}
          aria-label="Edit queued message"
        >
          <Pencil className="icon-xs" />
        </Button>
        {onSendNow && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-foreground"
            onClick={() => onSendNow(message.id)}
            aria-label="Send queued message now"
            title="Redirect agent: send this message now"
          >
            <ArrowUp className="icon-xs" />
          </Button>
        )}
        <ConfirmDialog
          trigger={
            <Button
              variant="ghost"
              size="icon"
              // Nudged away from "send now": the two sit a few pixels apart, and
              // the destructive one shouldn't be the easy mis-click.
              className="ml-1 h-6 w-6 text-muted-foreground hover:text-destructive"
              aria-label="Delete queued message"
            >
              <X className="icon-xs" />
            </Button>
          }
          title="Discard queued message?"
          description="It won't be sent, and this can't be undone."
          body={<QueuedMessagePreview message={message} attachments={attachments} />}
          confirmLabel="Discard"
          onConfirm={() => onDelete(message.id)}
        />
      </div>
    </div>
  );
}
